import React from 'react'
import { Skeleton, Form, Input, message } from 'antd'
import * as deepObject from 'deep-object-diff'
import moment from 'moment'
import passwordGen from 'generate-password'
import { ShelterConstants } from '../../../helpers/enums/Shelter'
import { FormComponent, BaseFormComponentProps } from '../../BaseComponent'
import StepForm from '../../StepsForm'
import IShelter from '../../../interfaces/IShelter'
import ILanguage from '../../../interfaces/ILanguage'
import { SearchRequest, ShelterCreateRequest, ShelterVerifySlugRequest } from '../../../services/api/interfaces/ApiRequest'
import { AddressProvider, AddressConsumer } from '../../../context/AddressContext'
import { CountryProvider, CountryConsumer } from '../../../context/CountryContext'
import { LanguageProvider, LanguageConsumer } from '../../../context/LanguageContext'

// Forms
import GeneralForm from './General'
import LocationForm from './Location'
import UserForm from './User'

type RegisterShelterProps = BaseFormComponentProps & {
  data?: IShelter | null,
  error: any | null,

  permissions: any | null,

  languages: ILanguage[] | null,
  validSlug: boolean | null,

  loading: boolean,
  contentLoading: boolean,
  history: any,

  loadLanguages: (args: SearchRequest) => void,
  verifySlug: (args: ShelterVerifySlugRequest) => Promise<boolean>,

  dispatchCreate: (args: ShelterCreateRequest) => Promise<any>
}

export default class RegisterShelter extends FormComponent<RegisterShelterProps> {

  static defaultProps = {
    contentLoading: true
  }

  state = {
    saving: false,
    loaded: false,
    address_uuid: "",
    countryUuid: null,
    languages: [] as any[]
  }

  private userForm: any


  componentDidMount() {
    const { loadLanguages } = this.props

    // Check permissions
    this.validateAccessPermissions()

    // Load langues
    loadLanguages({
      search: {
        deleted: false
      },
      options: {
        _force_ids: true
      }
    })
  }

  componentDidUpdate(prevProps: RegisterShelterProps, prevState) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    // Check permissions
    this.validateAccessPermissions()

    // We are loaded?
    if (!this.state.loaded) {
      // Load email template
      if (!this.props.contentLoading && !this.props.loading && !wasLoading) {
        newState.loaded = true
      }
    } else {
      // We are saving
      if (this.state.saving && wasLoading) {
        if (!this.props.error) {
          message.success('Organization shelter saved successfully!')

          // Update translations
          if (this.props.data) {
            this.props.history.replace(`/organization/shelter/${this.props.data.uuid}`)
          }
        } else {
          message.error('Failed to save organization shelter. Error: ' + this.props.error)
        }

        newState.saving = false
      }
    }

    // Update if changed
    const hasChanged = Object.keys(deepObject.diff(this.state, newState)).length > 0
    if (hasChanged) {
      this.setState(newState)
    }
  }

  handleStepProceed = (formName: string, values: any) => {
    if (formName === 'organization_shelter') {
      let newState = Object.assign({}, this.state)

      if (values?.slug && this.userForm) {
        this.userForm.ref.setFields([
          {
            name: 'email',
            value: `support+${values.slug}@adopets.com`
          }
        ])
      }

      if (values?.country_uuid) {
        newState.countryUuid = values.country_uuid
      }

      if (values?.languages) {
        newState.languages = values.languages.map(uuid => {
          return {
            uuid,
            main_language: values.main_language === uuid
          }
        })

        if (newState.languages.filter(p => p.main_language === true).length !== 1) {
          message.error(`Please don't try to trick me. Choose one main language please ;)`)
        }
      }

      this.setState(newState)
    }
  }

  handleSave = (values: any) => {
    const { dispatchCreate } = this.props
    const { languages } = this.state

    // Shelter settings
    const shelterSettings = Object.keys(values.organization_settings || []).map(key => {
      const settings = values.organization_settings[key]

      return {
        type_key: key.toUpperCase(),
        value: settings
      }
    })

    // Default settings
    shelterSettings.push(...[
      {
        type_key: 'APPLICATION_IN_PERSON',
        value: {
          new_visitor: 'WAIT_LIST',
          existing_visitor: [
            {
              current_status: 'MEETING_DONE',
              new_status: 'WAIT_LIST'
            },
            {
              current_status: 'CHECKED_IN',
              new_status: 'WAIT_LIST'
            }
          ]
        }
      },
      {
        type_key: 'ADOPTER_PERSONAL_DOCUMENTS',
        value: {
          state: {
            visible: true,
            required: true
          },
          document: {
            visible: true,
            required: true
          },
          state_uuid: {
            visible: true,
            required: true
          }
        }
      }
    ])

    const payload: ShelterCreateRequest = {
      organization_shelter: {
        ...values.organization_shelter,
        settings: shelterSettings,
        languages,
        branch: {
          ...values.organization_branch,
          opening_hour: values.organization_branch.opening_hour.map(openingHour => {
            return openingHour.closed ? null :{
              ...openingHour,
              opening: moment(openingHour.opening).format('HH:mm:ss'),
              closure: moment(openingHour.closure).format('HH:mm:ss')
            }
          }),
          address_uuid: this.state.address_uuid
        },
        support_account: values.organization_support_user,
        owner_account: values.organization_owner_user
      }
    }

    dispatchCreate(payload)
    this.setState({
      saving: true
    })
  }

  renderUser = () => {
    return (
      <Form.Item
        label="Name"
        name="name"
        rules={[
          {
            required: true,
            message: 'Required field'
          }
        ]}
      >
        <Input />
      </Form.Item>
    )
  }

  render() {
    const { loading, validSlug, verifySlug } = this.props
    const { loaded, countryUuid } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    const locationOpeningHourValues = ShelterConstants.DEFAULT_OPENING_HOUR.map(item => {
      const defaultOpening = moment(item.opening, 'HH:mm:sss')
      const defaultClosure = moment(item.closure, 'HH:mm:sss')

      return {
        weekday_key: item.weekday_key,
        opening: defaultOpening,
        closure: defaultClosure
      }
    })

    const generalValues = {
      active: true,
      nonprofit: null,
      descrliption: null
    }


    const locationValues = {
      opening_hour: locationOpeningHourValues,
      active: true,
      headquarter: true
    }

    const userValues = {
      first_name: 'Adopets',
      last_name: 'Support',
      email: '',
      password: passwordGen.generate({
        length: 10
      })
    }

    return (
      <AddressProvider>
        <CountryProvider>
          <LanguageProvider>
            <CountryConsumer>
              {({ fetching: fetchingCountry, countries, requestCountryList }) => (
                <LanguageConsumer>
                  {({ fetching: fetchingLanguage, languages, requestLanguageList }) => (
                    <AddressConsumer>
                      {({ fetching: fetchingAddress, addressList, requestAddress }) => (
                        <StepForm
                          loading={loading}
                          current={0}
                          steps={[
                            {
                              title: 'General',
                              formName: 'organization_shelter',
                              formValues: generalValues,
                              render: (form) => (
                                <GeneralForm
                                  form={form}

                                  contentLoading={fetchingCountry || fetchingLanguage}

                                  countries={countries}
                                  languages={languages}
                                  validSlug={validSlug}

                                  verifySlug={verifySlug}
                                  loadCountries={requestCountryList}
                                  loadLanguages={requestLanguageList}
                                />
                              )
                            },
                            {
                              title: 'Location',
                              formName: 'organization_branch',
                              formValues: locationValues,
                              render: (form) => (
                                <LocationForm
                                  form={form}
                                  addressList={addressList}
                                  countries={countries}
                                  countryUuid={countryUuid}
                                  loading={fetchingAddress}
                                  parent={this}
                                  findAddress={requestAddress}
                                  onSelectAddress={(addressList) => {
                                    this.setState({
                                      selectedAddress: addressList
                                    })
                                  }}
                                />
                              )
                            },
                            {
                              title: 'Adopets Support User',
                              formName: 'organization_support_user',
                              formValues: userValues,
                              render: (form: any) => {
                                this.userForm = form
                                return <UserForm />
                              }
                            }
                          ]}
                          onProceed={this.handleStepProceed}
                          onFinish={(values) => this.handleSave(values)}
                        />
                      )}
                    </AddressConsumer>
                  )}
                </LanguageConsumer>
              )}
            </CountryConsumer>
          </LanguageProvider>
        </CountryProvider>
      </AddressProvider>
    )
  }
}